import { EventInput } from '@fullcalendar/core';
import Appointment, { status } from '@spike/appointment-model';
import { BusinessHours, MarketplaceSchedule, Period, Week } from '@spike/model';
import Staff, { StaffSchedule, StaffSlot } from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import { SlotGroup, TempNewBooking } from './model';
import { BlockCalendar } from '@spike/block-calendar-model';
import isEmpty from 'lodash/isEmpty';
import { EventContentArg } from '@fullcalendar/core';
import EventFullCalendar from './EventFullCalendar';
import BlockEventFullCalendar from './BlockEventFullCalendar';
import TempEventFullCalendar from './TempEventFullCalendar';
import { EventSlotFullCalendar } from 'components/Calendar3/FullCalendar/EventSlotFullCalendar';
import {
    getDefaultSlotDate,
    getStaffSlotsByPeriod,
    isDefaultSlot
} from 'components/StaffSchedule/utils/StaffScheduleUtils';
import { EventSlotGroupFullCalendar } from 'components/Calendar3/FullCalendar/EventSlotGroupFullCalendar';
import { isSessionBusinessArea, SERVICES_BUSINESS_AREAS } from 'model/Service';

export const daysOfWeek = new Map([
    ['monday', 1],
    ['tuesday', 2],
    ['wednesday', 3],
    ['thursday', 4],
    ['friday', 5],
    ['saturday', 6],
    ['sunday', 0]
]);

export const convertBusinessHours = (day: string, businessHours: BusinessHours): EventInput => {
    return {
        daysOfWeek: [daysOfWeek.get(day)],
        startTime: businessHours.open,
        endTime: businessHours.close
    };
};

export const getDateBusinessHours = (date: Moment, schedule: MarketplaceSchedule): Period | null => {
    const day = date.format('YYYY-MM-DD');
    const weekDay = date.format('dddd').toLowerCase();

    const specialHour = schedule.specialHours.find(sh => sh.date === day);
    const businessHours = schedule.week[weekDay as keyof Week];

    const buildPeriod = (open?: string, close?: string): Period | null => {
        if (!open || !close) {
            return null;
        }

        const from = moment.tz(`${day} ${open}`, 'YYYY-MM-DD HH:mm', schedule.timeZone);
        const to = moment.tz(`${day} ${close}`, 'YYYY-MM-DD HH:mm', schedule.timeZone);

        if (!from.isValid() || !to.isValid()) {
            return null;
        }

        return { from, to };
    };

    if (specialHour) {
        return specialHour.isClosed
            ? null
            : buildPeriod(specialHour.businessHours?.open, specialHour.businessHours?.close);
    }

    return buildPeriod(businessHours?.open, businessHours?.close);
};

export const getWeekBusinessHours = (
    sunday: Moment,
    schedule: MarketplaceSchedule,
    calendarTimeZone: string
): Array<EventInput> => {
    const weekBusinessHours = Array.from(daysOfWeek.keys()).reduce((map, weekDay) => {
        const date = sunday.clone().add(daysOfWeek.get(weekDay), 'day');
        map.set(daysOfWeek.get(weekDay) ?? -1, getDateBusinessHours(date, schedule));
        return map;
    }, new Map<number, Period | null>());

    return Array.from(weekBusinessHours.entries()).flatMap(([key, period]) =>
        period
            ? [
                  {
                      daysOfWeek: [key],
                      startTime: period.from.clone().tz(calendarTimeZone).format('HH:mm:ss'),
                      endTime: period.to.clone().tz(calendarTimeZone).format('HH:mm:ss')
                  }
              ]
            : []
    );
};

export const getResourcePeriod = (date: Moment, schedule: StaffSchedule, timeZone: string): Period => {
    const weekDay = date.format('dddd').toLowerCase();

    const customDay = schedule.customDays.find(customDay =>
        customDay.from.clone().startOf('day').isSame(date.clone().startOf('day'))
    );
    const businessHours = schedule.default[weekDay as keyof Week];

    const dayOff = {
        from: date.clone().startOf('day'),
        to: date.clone().startOf('day')
    };

    if (customDay) {
        return customDay.on
            ? {
                  from: customDay.from.clone(),
                  to: customDay.to.clone()
              }
            : dayOff;
    } else if (businessHours === null || businessHours === undefined) {
        return dayOff;
    } else {
        return {
            from: moment.tz(`${date.format('YYYY-MM-DD')} ${businessHours?.open}`, 'YYYY-MM-DD HH:mm', timeZone),
            to: moment.tz(`${date.format('YYYY-MM-DD')} ${businessHours?.close}`, 'YYYY-MM-DD HH:mm', timeZone)
        };
    }
};

export const getResourceBusinessHours = (
    date: Moment,
    schedule: StaffSchedule,
    timeZone: string
): Array<EventInput> => {
    const period = getResourcePeriod(date, schedule, timeZone);

    return [
        {
            daysOfWeek: [daysOfWeek.get(date.format('dddd').toLowerCase())],
            startTime: period.from.clone().tz(timeZone).format('HH:mm:ss'),
            endTime: period.to.clone().tz(timeZone).format('HH:mm:ss')
        }
    ];
};

const camelize = (str: string | undefined) => (str ? str.replace(/\b(\w)/g, s => s.toUpperCase()) : '');

const notDraggableStates = [
    status.CANCELLED,
    status.COMPLETED,
    status.DECLINED,
    status.NO_SHOW,
    status.READY_FOR_CHECK_OUT,
    status.PAYMENT
];

export const convertToEvent = (
    appointment: Appointment,
    timeZone: string,
    format: string,
    multipleBookingsIds: Array<number>
): EventInput => {
    const appointmentFrom = appointment.duration.from.clone();
    const appointmentTo = appointment.duration.to.clone();
    const areaId = appointment.services[0].businessArea?.id;
    const isSession = isSessionBusinessArea(areaId);
    const sessionDetails =
        areaId === SERVICES_BUSINESS_AREAS.TRAINING ? appointment.details?.training : appointment.details?.walking;
    const sessionDuration = Number(sessionDetails?.duration ?? 0);
    const hasSessionDuration = Number.isFinite(sessionDuration) && sessionDuration > 0;

    const eventStart = appointmentFrom.clone();
    const eventEnd =
        isSession && hasSessionDuration ? appointmentTo.clone().add(sessionDuration, 'minutes') : appointmentTo.clone();

    return {
        id: appointment.id?.toString() ?? '0',
        appointmentId: appointment.id,
        appointmentUuid: appointment.uuid,
        bookingId: appointment.bookingId,
        start: eventStart.toDate(),
        end: eventEnd.toDate(),
        title: `${appointment.pet.name} - ${appointment.services[0].name}`,
        resourceId: appointment.services[0].staff?.id?.toString() ?? '0',
        timeZone: timeZone,
        petId: appointment.pet.id,
        petName: appointment.pet.name,
        petType: appointment.pet.type,
        petBreed: appointment.pet.breed?.name,
        petHasMedicalConditions: appointment.pet.hasMedicalConditions,
        staffAvatar: appointment.services[0].staff?.avatar,
        staffId: appointment.services[0].staff?.id,
        serviceId: appointment.services[0].id,
        serviceName: appointment.services[0].name,
        client: appointment.customer,
        status: { ...appointment.status },
        from: appointmentFrom,
        to: appointmentTo,
        startEditable: !notDraggableStates.includes(appointment.status.id),
        temp: false,
        type: 'appointment',
        recurringUuid: appointment.recurringUuid,
        format: format,
        isMultipleBooking: multipleBookingsIds.includes(appointment.bookingId),
        isSession,
        petHasNegativePetTags: appointment.pet.hasNegativePersonalities,
        isInHomeSession: isSession && sessionDetails?.type === 'in_home'
    };
};

export const convertToTempEvent = (tempNewBooking: TempNewBooking, timeZone: string): EventInput => {
    const end = tempNewBooking.at.clone().add(tempNewBooking.durationMinutes, 'minutes');

    return {
        id: 'tmp',
        start: tempNewBooking.at.clone().toDate(),
        end: end.clone().toDate(),
        title: `New Booking`,
        resourceId: tempNewBooking.staffId?.toString() ?? '0',
        timeZone: timeZone,
        from: tempNewBooking.at.clone(),
        to: end.clone(),
        startEditable: false,
        temp: true,
        type: 'temporal'
    };
};

export const convertToBlockCalendarEvent = (blockCalendar: BlockCalendar, timeZone: string): EventInput => ({
    id: `block_${blockCalendar.id?.toString() ?? '0'}`,
    blockId: blockCalendar.id,
    blockUuid: blockCalendar.uuid,
    start: blockCalendar.start.clone().toDate(),
    end: blockCalendar.end.clone().toDate(),
    title: isEmpty(blockCalendar.title) ? 'No Title' : camelize(blockCalendar.title),
    resourceId: blockCalendar.staff.id.toString() ?? '0',
    timeZone: timeZone,
    staffAvatar: blockCalendar.staff.image,
    staffId: blockCalendar.staff.id,
    from: blockCalendar.start.clone(),
    to: blockCalendar.end.clone(),
    recurringUuid: blockCalendar.recurringUuid,
    frequency: blockCalendar.frequency,
    startEditable: true,
    temp: false,
    type: 'block'
});

export const convertToSlotEvent = (slot: StaffSlot, date: Moment, staffId: number, timeZone: string): EventInput => {
    const slotDate = isDefaultSlot(slot) ? getDefaultSlotDate(date, slot) : slot.date;

    const id = `slot_${staffId}-${slotDate.format('YYYY-MM-DD')}-${slot.time.replace(':', '')}`;

    return {
        id,
        type: 'slot',
        title: `Slot`,
        start: slotDate.clone().toDate(),
        end: slotDate.clone().add(15, 'minutes').toDate(),
        timeZone: timeZone,
        resourceId: staffId.toString(),
        startEditable: false,
        temp: false,
        extendedProps: {
            slot,
            staffId: staffId,
            from: slotDate.clone(),
            to: slotDate.clone().add(15, 'minutes')
        }
    };
};

export const convertToSlotGroupEvents = (staffs: Array<Staff>, period: Period, timeZone: string): Array<EventInput> => {
    const periodFrom = period.from;

    const slotGroups: Record<string, SlotGroup> = {};

    for (const staff of staffs) {
        const slots = getStaffSlotsByPeriod(staff.slots, period);

        for (const slot of slots) {
            const slotDate = isDefaultSlot(slot) ? getDefaultSlotDate(periodFrom, slot) : slot.date;
            const key = `${slotDate.format('YYYY-MM-DD')}-${slot.time}`;

            if (!slotGroups[key]) {
                slotGroups[key] = {
                    time: slot.time,
                    date: slotDate.clone(),
                    staffSlots: [{ staff, slot }]
                };
            } else {
                slotGroups[key].staffSlots.push({ staff, slot });
            }
        }
    }

    return Object.values(slotGroups).map(group => ({
        id: `slot_group_${group.date.format('YYYY-MM-DD')}-${group.time.replace(':', '')}`,
        title: 'Slot',
        type: 'slot-group',
        start: group.date.clone().toDate(),
        end: group.date.clone().add(15, 'minutes').toDate(),
        timeZone,
        extendedProps: { slotGroup: group }
    }));
};

export const renderEvent = (
    args: EventContentArg,
    appointments?: Array<Appointment>,
    loadingAppointmentIds?: Array<number>
) => {
    switch (args.event.extendedProps.type) {
        case 'temporal':
            return <TempEventFullCalendar {...args} />;
        case 'appointment':
            return (
                <EventFullCalendar
                    {...args}
                    loading={loadingAppointmentIds?.includes(args.event.extendedProps.appointmentId)}
                />
            );
        case 'block':
            return (
                <BlockEventFullCalendar
                    {...args}
                    loading={false}
                />
            );
        case 'slot':
            return (
                <EventSlotFullCalendar
                    appointments={appointments ?? []}
                    {...args}
                />
            );
        case 'slot-group':
            return (
                <EventSlotGroupFullCalendar
                    appointments={appointments ?? []}
                    {...args}
                />
            );
        default:
            return null;
    }
};
